import { FormEvent, useMemo } from 'react';
import { Loader2 } from 'lucide-react';
import InputField from '../../../components/Forms/Input/InputField';
import ToggleSwitch from '../../../components/Forms/Input/ToggleSwitch';
import FormModal from '../../../components/Modals/FormModal';
import RestaurantFormHelp from '../shared/RestaurantFormHelp';
import PrinterQuickField from './PrinterQuickField';
import { RestaurantPreparationStationForm } from '../../../types/restaurant/RestaurantPreparationStationTypes';
import {
  RestaurantPrinterDTO,
  RestaurantPrinterForm,
} from '../../../types/restaurant/RestaurantPrinterTypes';

interface Props {
  isOpen: boolean; form: RestaurantPreparationStationForm;
  printers: RestaurantPrinterDTO[]; saving: boolean; onClose: () => void;
  onChange: <K extends keyof RestaurantPreparationStationForm>(field: K, value: RestaurantPreparationStationForm[K]) => void;
  onCreatePrinter: (
    input: Partial<RestaurantPrinterForm> & { name: string }
  ) => Promise<RestaurantPrinterDTO | null>;
  onSave: () => void;
}

const PreparationStationFormModal = ({ isOpen, form, printers, saving, onClose, onChange, onCreatePrinter, onSave }: Props) => {
  const errors = useMemo(() => {
    const next: Record<string, string> = {};
    if (!form.name.trim()) next.name = 'El nombre es obligatorio.';
    else if (form.name.trim().length > 100) next.name = 'Máximo 100 caracteres.';
    if (form.estimatedPreparationMinutes !== '' && (Number(form.estimatedPreparationMinutes) < 1 || Number(form.estimatedPreparationMinutes) > 1440))
      next.estimatedPreparationMinutes = 'Debe estar entre 1 y 1440 minutos.';
    return next;
  }, [form]);

  const submit = (event: FormEvent) => { event.preventDefault(); if (!Object.keys(errors).length) onSave(); };

  return (
    <FormModal isOpen={isOpen} onClose={saving ? undefined : onClose} title={form.id ? 'Editar estación de preparación' : 'Nueva estación de preparación'} maxWidth="max-w-3xl" headerExtra={<RestaurantFormHelp topic="preparationStation" />}>
      <form onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-1 gap-x-5 md:grid-cols-2">
          <InputField label="Nombre" name="name" type="text" iconKey="list" value={form.name} required error={errors.name} placeholder="Ej. Cocina principal" onChange={(e) => onChange('name', e.target.value)} />
          {form.id && (
            <InputField label="Código" name="code" type="text" iconKey="code" value={form.code ?? ''} disabled onChange={() => undefined} />
          )}
          <PrinterQuickField
            printers={printers}
            value={form.printerId}
            disabled={saving}
            onChange={(printerId) => onChange('printerId', printerId)}
            onCreatePrinter={onCreatePrinter}
          />
          <InputField label="Tiempo estimado (minutos)" name="estimatedPreparationMinutes" type="number" iconKey="clock" value={form.estimatedPreparationMinutes} min={1} max={1440} error={errors.estimatedPreparationMinutes} onChange={(e) => onChange('estimatedPreparationMinutes', e.target.value ? Number(e.target.value) : '')} />
          <InputField label="Orden de visualización" name="displayOrder" type="number" iconKey="list" value={form.displayOrder} min={0} onChange={(e) => onChange('displayOrder', Math.max(0, Number(e.target.value)))} />
          <div className="mb-4 rounded-lg border border-stroke px-4 py-3 dark:border-strokedark">
            <ToggleSwitch label="Disponible para recibir órdenes" name="stationEnabled" checked={form.enabled} onChange={() => onChange('enabled', !form.enabled)} />
          </div>
          {form.id && (
            <div className="mb-4 rounded-lg border border-stroke px-4 py-3 dark:border-strokedark md:col-span-2">
              <ToggleSwitch label="Registro activo" name="stationActive" checked={form.active ?? true} onChange={() => onChange('active', !(form.active ?? true))} />
            </div>
          )}
        </div>
        <div>
          <label className="mb-3 block text-black dark:text-white">Descripción</label>
          <textarea rows={3} maxLength={300} value={form.description} onChange={(e) => onChange('description', e.target.value)} className="w-full rounded border border-stroke bg-transparent px-5 py-3 text-black outline-none focus:border-primary dark:border-strokedark dark:text-white" placeholder="Área, ubicación o indicaciones..." />
          <p className="mt-1 text-right text-xs text-bodydark2">{form.description.length}/300</p>
        </div>
        <div className="flex justify-end gap-3 border-t border-stroke pt-5 dark:border-strokedark">
          <button type="button" onClick={onClose} disabled={saving} className="rounded border border-stroke px-5 py-2.5 font-medium text-black dark:border-strokedark dark:text-white">Cancelar</button>
          <button type="submit" disabled={saving || !!Object.keys(errors).length} className="inline-flex items-center gap-2 rounded bg-primary px-5 py-2.5 font-medium text-white disabled:opacity-50">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}{saving ? 'Guardando...' : 'Guardar estación'}
          </button>
        </div>
      </form>
    </FormModal>
  );
};

export default PreparationStationFormModal;
